import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';

import { useRetailContext } from 'common/hooks';
import { useCanViewPurchase, MessagePage } from 'modules/experience-core';
import { ExperienceVenueEntity, PageHeader } from 'modules/experience-core';
import { useExperienceModuleContext } from 'modules/experience-core';

import { isoDateToJsDate, Period } from './FilterDateRangePicker';
import { HeaderActions } from './HeaderActions';
import PurchasesPage from './PurchasesPage';
import RootRedirector from './RootRedirector';
import { EARLIEST_SUPPORTED_DATETIME_FROM_NOW_IN_DAYS } from './constants';
import { purchasesQueryConfig } from './purchasesQueryConfig';
import { periodToIsoDateRange } from './utils';

const DEFAULT_PERIOD: Period = 'this-week';

const Purchases = ({ experienceVenueEntity }: { experienceVenueEntity: ExperienceVenueEntity }) => {
  const venue = experienceVenueEntity.experienceEntityObject;
  const { url: baseUrl } = useRouteMatch();
  const [now] = useState(Date.now());
  const history = useHistory();
  const [query, setQuery] = useQueryParams(purchasesQueryConfig);
  const [isBarCodesModalOpen, setIsBarCodesModalOpen] = useState(false);
  const isRetail = useRetailContext();

  const purchasesRouteMatch = useRouteMatch<{ period: string; filters: string }>(`${baseUrl}/:period`);

  const dateRange = purchasesRouteMatch
    ? periodToIsoDateRange(venue.timezone, now, purchasesRouteMatch.params.period)
    : undefined;

  const isDateRangeIntersectsWithAllowedRange = useMemo(() => {
    if (dateRange) {
      const fromDate = DateTime.fromJSDate(isoDateToJsDate(dateRange.from, venue.timezone));
      const minimumAllowedStartDate = DateTime.fromMillis(now).minus({
        days: EARLIEST_SUPPORTED_DATETIME_FROM_NOW_IN_DAYS,
      });
      return fromDate.startOf('day') < minimumAllowedStartDate.startOf('day');
    }
    return false;
  }, [dateRange, now, venue.timezone]);

  const generatePurchasesPeriodHref = useCallback(
    (period: string) => {
      return purchasesRouteMatch
        ? `${generatePath(purchasesRouteMatch.path, { ...purchasesRouteMatch.params, period })}${location.search}`
        : `${baseUrl}/${period}`;
    },
    [baseUrl, purchasesRouteMatch],
  );

  const handleDateRangeChange = useCallback(
    (period: string) => {
      const newUrl = generatePurchasesPeriodHref(period);
      history.push(newUrl);
    },
    [history, generatePurchasesPeriodHref],
  );

  useEffect(() => {
    if (purchasesRouteMatch?.params.period && !dateRange) {
      handleDateRangeChange(DEFAULT_PERIOD);
    }
  }, [dateRange, handleDateRangeChange, purchasesRouteMatch?.params.period]);

  return (
    <RootRedirector
      rootRoute={baseUrl}
      defaultSubRoute={`/${DEFAULT_PERIOD}`}
      localStorageKey="purchases.previousQuery"
    >
      {dateRange && (
        <>
          <PageHeader>
            <HeaderActions
              dateRange={dateRange}
              handleDateRangeChange={handleDateRangeChange}
              query={query}
              isDateRangeIntersectsWithAllowedRange={isDateRangeIntersectsWithAllowedRange}
              isRetail={isRetail}
              setIsBarCodesModalOpen={setIsBarCodesModalOpen}
              venue={venue}
            />
          </PageHeader>
          <PurchasesPage
            isBarCodesModalOpen={isBarCodesModalOpen}
            venue={venue}
            dateRange={dateRange}
            query={query}
            isDateRangeIntersectsWithAllowedRange={isDateRangeIntersectsWithAllowedRange}
            setQuery={setQuery}
            setIsBarCodesModalOpen={setIsBarCodesModalOpen}
            handleDateRangeChange={handleDateRangeChange}
          />
        </>
      )}
    </RootRedirector>
  );
};

export const ExperiencePurchases = () => {
  const { selectedExperienceVenueEntity } = useExperienceModuleContext();
  const canViewPurchase = useCanViewPurchase(selectedExperienceVenueEntity ? [selectedExperienceVenueEntity] : []);

  if (!selectedExperienceVenueEntity) {
    return <MessagePage variant="error" />;
  }

  return canViewPurchase ? (
    <Purchases experienceVenueEntity={selectedExperienceVenueEntity} />
  ) : (
    <MessagePage variant="missing_permissions" />
  );
};
